/**
 * THE NAMING BRIEF ON THE CANVAS — the deliverable the customer takes away.
 *
 * It renders the STORED brief, fetched by id, rather than anything retyped into
 * the surface props. That is the whole design: the brief is authored once by the
 * `naming_brief` tool, and this screen, the downloaded document and the emailed
 * copy are three views of the same object. A finding cannot say "confirmed" here
 * and "not verified" in the file.
 *
 * The two controls are real or they are absent. Download mints a presigned URL
 * for the stored document and saves it. Email dispatches a surface action that
 * genuinely attempts a send, and reports back what happened — including "it did
 * not go out", which is a normal outcome and never dressed up as success.
 */
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AlertCircle, ArrowRight, Check, Download, Loader2, Mail } from 'lucide-react';
import { fetchPresignedUrl } from '@/app/lib/services/presigned-url';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { trpc } from '@/app/lib/trpc';
import { cn } from '@/app/lib/utils';
import { CanvasFieldLabel, CanvasSectionLabel, StatusTag } from './canvas-type.tsx';
import type { EvidenceStatus } from './canvas-type.tsx';

const messages = defineMessages({
  eyebrow: {
    id: 'studio.brief.eyebrow',
    defaultMessage: 'NAMING BRIEF',
    description: 'Small caps label above the selected name on the deliverable',
  },
  selectedName: {
    id: 'studio.brief.selectedName',
    defaultMessage: 'SELECTED NAME',
    description: 'Label under the chosen name on the deliverable',
  },
  business: {
    id: 'studio.brief.business',
    defaultMessage: 'THE BUSINESS',
    description: 'Deliverable section heading',
  },
  objective: {
    id: 'studio.brief.objective',
    defaultMessage: 'NAMING OBJECTIVE',
    description: 'Deliverable section heading',
  },
  taste: {
    id: 'studio.brief.taste',
    defaultMessage: 'TASTE PROFILE',
    description: 'Deliverable section heading',
  },
  why: {
    id: 'studio.brief.why',
    defaultMessage: 'WHY THIS NAME',
    description: 'Deliverable section heading',
  },
  research: {
    id: 'studio.brief.research',
    defaultMessage: 'RESEARCH SNAPSHOT',
    description: 'Deliverable section heading',
  },
  learnings: {
    id: 'studio.brief.learnings',
    defaultMessage: 'IMPORTANT LEARNINGS',
    description: 'Deliverable section heading',
  },
  nextSteps: {
    id: 'studio.brief.nextSteps',
    defaultMessage: 'NEXT STEPS',
    description: 'Deliverable section heading',
  },
  download: {
    id: 'studio.brief.download',
    defaultMessage: 'Download the brief',
    description: 'Button that saves the brief document',
  },
  downloading: {
    id: 'studio.brief.downloading',
    defaultMessage: 'Preparing…',
    description: 'Button label while the download URL is being minted',
  },
  downloadFailed: {
    id: 'studio.brief.downloadFailed',
    defaultMessage: 'The download did not start. Try again in a moment.',
    description: 'Shown when minting the download URL failed',
  },
  emailIt: {
    id: 'studio.brief.emailIt',
    defaultMessage: 'Email it to me',
    description: 'Button that reveals the email field',
  },
  emailPlaceholder: {
    id: 'studio.brief.emailPlaceholder',
    defaultMessage: 'your@email.com',
    description: 'Placeholder of the email field on the deliverable',
  },
  emailSend: {
    id: 'studio.brief.emailSend',
    defaultMessage: 'Send',
    description: 'Button that sends the brief by email',
  },
  emailSending: {
    id: 'studio.brief.emailSending',
    defaultMessage: 'Sending…',
    description: 'Label while the send is in flight',
  },
  emailAsked: {
    id: 'studio.brief.emailAsked',
    defaultMessage: 'Asked the agent to send it — watch the panel for confirmation.',
    description: 'Shown after the email request is dispatched to the agent',
  },
  emailInvalid: {
    id: 'studio.brief.emailInvalid',
    defaultMessage: 'That address does not look right.',
    description: 'Inline validation for the email field',
  },
  unavailable: {
    id: 'studio.brief.unavailable',
    defaultMessage: 'This brief is no longer available to load.',
    description: 'Shown when the stored brief cannot be read',
  },
  loading: {
    id: 'studio.brief.loading',
    defaultMessage: 'Loading the brief…',
    description: 'Shown while the stored brief is being fetched',
  },
  disclaimer: {
    id: 'studio.brief.disclaimer',
    defaultMessage:
      'Research recorded here is preliminary screening carried out during this engagement. It is not a trademark search, a legal opinion, or clearance to use this name. Obtain formal trademark clearance from a qualified attorney before committing to registration, spend, or launch.',
    description: 'The standing legal qualification carried on the deliverable',
  },
});

interface BriefFinding {
  label: string;
  value: string;
  status: EvidenceStatus;
}

interface NamingBrief {
  id: string;
  createdAt: string;
  selectedName: string;
  business: string;
  objective: string;
  tasteProfile: string;
  whyThisName: string;
  research: BriefFinding[];
  learnings: string[];
  nextSteps: string[];
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** The stored document lives in common storage, so the presigned lookup needs the
 *  `common/` branch prefix the route resolves. */
function documentPath(briefId: string): string {
  return `common/briefs/${briefId}.html`;
}

function filename(brief: NamingBrief): string {
  const slug =
    brief.selectedName
      .normalize('NFKD')
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: stripping combining marks
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'Name';
  return `Naming-Brief-${slug}.html`;
}

/** A prose section. Rendered only when it has content — an empty section is
 *  absent, never a heading with nothing under it. */
const Section: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <section className="mt-8 first:mt-0">
    <CanvasSectionLabel className="block">{label}</CanvasSectionLabel>
    <div className="mt-2.5 max-w-[68ch] text-sm leading-relaxed text-canvas-ink">{children}</div>
  </section>
);

export const BriefDeliverable: FC<{ briefId: string }> = ({ briefId }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const [brief, setBrief] = useState<NamingBrief | null | undefined>(undefined);
  const [downloadState, setDownloadState] = useState<'idle' | 'busy' | 'error'>('idle');
  // Whether emailing is genuinely wired up. Starts `false` and only turns true on
  // a real answer: the control must never appear on an assumption, because an
  // "Email it to me" button that cannot send is the dishonest kind.
  const [emailAvailable, setEmailAvailable] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [address, setAddress] = useState('');
  const [emailState, setEmailState] = useState<'idle' | 'invalid' | 'sent'>('idle');
  // `disabled` only applies on the next render, so two fast clicks can both enter
  // the handler. A ref closes that window.
  const inFlight = useRef(false);

  useEffect(() => {
    let live = true;
    trpc.namingBrief.get
      .query({ briefId })
      .then((result) => {
        if (live) {
          setBrief((result as NamingBrief | null) ?? null);
        }
      })
      .catch(() => {
        if (live) {
          setBrief(null);
        }
      });
    return () => {
      live = false;
    };
  }, [briefId]);

  // Ask whether a send is actually possible before offering it.
  useEffect(() => {
    let live = true;
    trpc.namingBrief.emailAvailable
      .query()
      .then((result) => {
        if (live) {
          setEmailAvailable(result.available === true);
        }
      })
      .catch(() => {
        if (live) {
          setEmailAvailable(false);
        }
      });
    return () => {
      live = false;
    };
  }, []);

  const handleDownload = useCallback(async () => {
    if (inFlight.current || !brief) {
      return;
    }
    inFlight.current = true;
    setDownloadState('busy');
    try {
      const { url } = await fetchPresignedUrl(documentPath(brief.id), 'attachment');
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.rel = 'noopener';
      anchor.download = filename(brief);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setDownloadState('idle');
    } catch {
      // No silent failure: a download that did not happen must say so, because
      // the customer's only other copy is this screen.
      setDownloadState('error');
    } finally {
      inFlight.current = false;
    }
  }, [brief]);

  const handleSend = useCallback(() => {
    const value = address.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setEmailState('invalid');
      return;
    }
    // The agent owns the send and reports the real outcome in the panel — this
    // button only carries the address the customer typed.
    dispatch?.('emailBrief', { briefId, to: value });
    setEmailState('sent');
  }, [address, briefId, dispatch]);

  if (brief === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-canvas-meta/70">
        <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
        {intl.formatMessage(messages.loading)}
      </p>
    );
  }

  if (brief === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-canvas-meta">
        <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 flex-none" />
        {intl.formatMessage(messages.unavailable)}
      </p>
    );
  }

  return (
    <article className="min-w-0 animate-fadeUp motion-reduce:animate-none">
      {/* The deliverable is framed as a document: a hairline sheet inside the
          canvas, so it reads as the thing you keep rather than another block. */}
      <div className="rounded-[10px] border border-canvas-meta/30 px-6 py-9 sm:px-10 sm:py-12">
        <CanvasFieldLabel className="block">
          {intl.formatMessage(messages.eyebrow)}
        </CanvasFieldLabel>
        <h3
          className="mt-6 text-4xl font-bold leading-[0.98] tracking-tight text-canvas-ink sm:text-6xl"
          translate="no"
        >
          {brief.selectedName}
        </h3>
        <CanvasSectionLabel className="mt-5 block">
          {intl.formatMessage(messages.selectedName)}
        </CanvasSectionLabel>

        <hr className="mt-9 border-0 border-t border-canvas-meta/25" aria-hidden="true" />

        <div className="mt-8">
          {brief.business ? (
            <Section label={intl.formatMessage(messages.business)}>{brief.business}</Section>
          ) : null}
          {brief.objective ? (
            <Section label={intl.formatMessage(messages.objective)}>{brief.objective}</Section>
          ) : null}
          {brief.tasteProfile ? (
            <Section label={intl.formatMessage(messages.taste)}>{brief.tasteProfile}</Section>
          ) : null}
          {brief.whyThisName ? (
            <Section label={intl.formatMessage(messages.why)}>{brief.whyThisName}</Section>
          ) : null}

          {/* Every row keeps the status it was screened with. This is the section
              most likely to be read as clearance, so the tag sits on the label
              where it qualifies the claim itself. */}
          {brief.research.length > 0 ? (
            <section className="mt-8">
              <CanvasSectionLabel className="block">
                {intl.formatMessage(messages.research)}
              </CanvasSectionLabel>
              <dl className="mt-3 flex flex-col gap-4">
                {brief.research.map((finding) => (
                  <div key={`${finding.label}-${finding.value}`} className="min-w-0">
                    <dt className="flex flex-wrap items-center gap-2">
                      <CanvasFieldLabel>{finding.label}</CanvasFieldLabel>
                      <StatusTag status={finding.status} />
                    </dt>
                    <dd className="mt-1 max-w-[68ch] text-sm leading-relaxed text-canvas-ink">
                      {finding.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          {brief.learnings.length > 0 ? (
            <section className="mt-8">
              <CanvasSectionLabel className="block">
                {intl.formatMessage(messages.learnings)}
              </CanvasSectionLabel>
              <ul className="mt-3 flex flex-col gap-2.5">
                {brief.learnings.map((learning) => (
                  <li key={learning} className="flex min-w-0 items-baseline gap-2.5">
                    <Check
                      aria-hidden="true"
                      className="mt-[0.15rem] h-3 w-3 flex-none text-canvas-meta"
                    />
                    <span className="max-w-[66ch] text-sm leading-relaxed text-canvas-ink">
                      {learning}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {brief.nextSteps.length > 0 ? (
            <section className="mt-8">
              <CanvasSectionLabel className="block">
                {intl.formatMessage(messages.nextSteps)}
              </CanvasSectionLabel>
              <ol className="mt-3 flex flex-col gap-3">
                {brief.nextSteps.map((step, index) => (
                  <li key={step} className="flex min-w-0 items-baseline gap-3.5">
                    <span className="text-xs font-medium tabular-nums leading-relaxed text-canvas-meta">
                      {index + 1}
                    </span>
                    <span className="max-w-[64ch] text-sm leading-relaxed text-canvas-ink">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>

        {/* The qualification travels with the document, on screen as well as in
            the file. Quiet, but never omitted. */}
        <p className="mt-10 border-l border-canvas-meta/30 pl-4 text-xs leading-relaxed text-canvas-meta/80">
          {intl.formatMessage(messages.disclaimer)}
        </p>
      </div>

      {/* Take it away. Both controls do a real thing or say they could not. */}
      <div className="mt-6 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloadState === 'busy'}
            className="inline-flex touch-manipulation items-center gap-2 rounded-sm border border-canvas-ink bg-canvas-ink px-4 py-2 text-xs font-semibold text-canvas transition-opacity duration-150 hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-ink disabled:cursor-wait disabled:opacity-70 active:scale-95"
          >
            {downloadState === 'busy' ? (
              <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            {intl.formatMessage(
              downloadState === 'busy' ? messages.downloading : messages.download,
            )}
          </button>

          {emailAvailable && !emailOpen ? (
            <button
              type="button"
              onClick={() => setEmailOpen(true)}
              className="inline-flex touch-manipulation items-center gap-2 rounded-sm border border-canvas-meta/40 px-4 py-2 text-xs font-medium text-canvas-meta transition-colors duration-150 hover:border-canvas-meta hover:bg-canvas-meta/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta active:scale-95"
            >
              <Mail aria-hidden="true" className="h-3.5 w-3.5" />
              {intl.formatMessage(messages.emailIt)}
            </button>
          ) : null}
        </div>

        {downloadState === 'error' ? (
          <p className="flex items-center gap-2 text-xs text-canvas-meta">
            <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 flex-none" />
            {intl.formatMessage(messages.downloadFailed)}
          </p>
        ) : null}

        {/* The address is typed by the customer, right here. The agent never
            supplies one, and nothing is sent until this button is pressed. */}
        {emailOpen && emailState !== 'sent' ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={address}
                onChange={(event) => {
                  setAddress(event.target.value);
                  setEmailState('idle');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={intl.formatMessage(messages.emailPlaceholder)}
                aria-label={intl.formatMessage(messages.emailPlaceholder)}
                aria-invalid={emailState === 'invalid'}
                className={cn(
                  'min-w-0 flex-1 rounded-sm border bg-transparent px-3 py-2 text-sm text-canvas-ink placeholder:text-canvas-meta/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta sm:max-w-xs',
                  emailState === 'invalid' ? 'border-canvas-ink' : 'border-canvas-meta/40',
                )}
              />
              <button
                type="button"
                onClick={handleSend}
                className="inline-flex touch-manipulation items-center gap-2 rounded-sm border border-canvas-meta/40 px-4 py-2 text-xs font-medium text-canvas-meta transition-colors duration-150 hover:border-canvas-meta hover:bg-canvas-meta/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta active:scale-95"
              >
                {intl.formatMessage(messages.emailSend)}
                <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            </div>
            {emailState === 'invalid' ? (
              <p className="text-xs text-canvas-meta">
                {intl.formatMessage(messages.emailInvalid)}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Dispatched, NOT delivered. The agent reports the actual result in the
            panel — including a failure — so this line never claims a send. */}
        {emailState === 'sent' ? (
          <p className="text-xs leading-relaxed text-canvas-meta/80">
            {intl.formatMessage(messages.emailAsked)}
          </p>
        ) : null}
      </div>
    </article>
  );
};
